import React from "react";
import {
  Box,
  Typography,
  TextField,
  IconButton,
  Skeleton,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Delete as DeleteIcon } from "@mui/icons-material";
import { Controller, Control, FieldArrayWithId } from "react-hook-form";
import { useCompanyProfile } from "../Dashboard/Charts/hooks/useCompanyProfile";
import { getSymbolImageUrl } from "../../lib/symbolImage";
import type { ManualHoldingsFormData } from "./schemas/manualHoldingsSchema";

interface ManualHoldingRowProps {
  field: FieldArrayWithId<ManualHoldingsFormData, "holdings", "id">;
  index: number;
  control: Control<ManualHoldingsFormData>;
  ticker: string | undefined;
  shares: number;
  averagePrice: number;
  onRemove: (index: number) => void;
}

const ManualHoldingRow: React.FC<ManualHoldingRowProps> = ({
  field,
  index,
  control,
  ticker,
  shares,
  averagePrice,
  onRemove,
}) => {
  const theme = useTheme();
  const { data: companyData, isLoading: isLoadingProfile } = useCompanyProfile(ticker || "");
  const profile = Array.isArray(companyData) ? companyData[0] : companyData;

  const flatUIColors = {
    background: theme.palette.mode === "dark"
      ? "rgba(255, 255, 255, 0.02)"
      : "rgba(0, 0, 0, 0.02)",
    border: theme.palette.mode === "dark"
      ? "rgba(255, 255, 255, 0.1)"
      : "rgba(0, 0, 0, 0.1)",
    textPrimary: theme.palette.mode === "dark" ? "#ffffff" : "#1d1d1f",
    textSecondary: theme.palette.mode === "dark" ? "#9ca3af" : "#64748b",
    textMuted: theme.palette.mode === "dark" ? "#6b7280" : "#94a3b8",
  };

  return (
    <Box
      sx={{
        display: "flex",
        gap: 2,
        alignItems: "center",
        py: 1.5,
        minHeight: 64,
      }}
    >
      {/* Company Logo */}
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: flatUIColors.background,
          border: `1px solid ${flatUIColors.border}`,
          flexShrink: 0,
        }}
      >
        {isLoadingProfile ? (
          <Skeleton
            variant="rectangular"
            width={32}
            height={32}
            sx={{ borderRadius: 1.5 }}
          />
        ) : profile?.image && !profile.defaultImage ? (
          <img
            src={getSymbolImageUrl(ticker ?? "")}
            alt={`${profile.companyName || ticker} logo`}
            style={{
              width: 32,
              height: 32,
              objectFit: "contain",
              borderRadius: 4,
            }}
          />
        ) : (
          <Typography
            sx={{
              fontSize: "0.875rem",
              fontWeight: 600,
              color: flatUIColors.textSecondary,
            }}
          >
            {ticker?.substring(0, 2)}
          </Typography>
        )}
      </Box>

      {/* Company Name & Ticker */}
      <Box sx={{ minWidth: 150, maxWidth: 200, flexShrink: 0 }}>
        <Typography
          sx={{
            fontSize: "0.875rem",
            fontWeight: 600,
            color: flatUIColors.textPrimary,
            lineHeight: 1.2,
            mb: 0.25,
          }}
        >
          {ticker}
        </Typography>
        {isLoadingProfile ? (
          <Skeleton width="80%" height={14} />
        ) : (
          <Typography
            sx={{
              fontSize: "0.8125rem",
              fontWeight: 400,
              color: flatUIColors.textSecondary,
              lineHeight: 1.2,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {profile?.companyName || "—"}
          </Typography>
        )}
      </Box>

      {/* Shares Input */}
      <Controller
        name={`holdings.${index}.shares`}
        control={control}
        render={({ field, fieldState: { error } }) => (
          <TextField
            {...field}
            value={field.value === 0 ? "" : field.value}
            onChange={(e) => {
              const value = e.target.value;
              field.onChange(value === "" ? 0 : parseFloat(value));
            }}
            type="number"
            size="small"
            label="Shares"
            placeholder="0"
            error={!!error}
            helperText={error?.message}
            inputProps={{
              min: 0.00001,
              step: "any",
            }}
            sx={{ flex: 1, minWidth: 120 }}
          />
        )}
      />

      {/* Average Price Input */}
      <Controller
        name={`holdings.${index}.averagePrice`}
        control={control}
        render={({ field, fieldState: { error } }) => (
          <TextField
            {...field}
            value={field.value === 0 ? "" : field.value}
            onChange={(e) => {
              const value = e.target.value;
              field.onChange(value === "" ? 0 : parseFloat(value));
            }}
            type="number"
            size="small"
            label="Avg Price (USD)"
            placeholder="0.00"
            error={!!error}
            helperText={error?.message}
            inputProps={{
              min: 0.01,
              step: "any",
            }}
            sx={{
              flex: 1,
              minWidth: 140,
              "& .MuiInputLabel-root": {
                fontSize: "0.8125rem",
              },
            }}
          />
        )}
      />

      {/* Cost Basis Display */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          minWidth: "100px",
          textAlign: "right",
          flexShrink: 0,
        }}
      >
        <Typography variant="caption" color="text.secondary">
          Cost Basis
        </Typography>
        <Typography variant="body1" sx={{ fontWeight: 600 }}>
          $
          {shares && averagePrice
            ? (shares * averagePrice).toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })
            : "0.00"}
        </Typography>
      </Box>

      {/* Remove Button */}
      <IconButton
        onClick={() => onRemove(index)}
        size="small"
        sx={{
          width: 36,
          height: 36,
          color: theme.palette.error.main,
          flexShrink: 0,
          transition: "all 0.15s cubic-bezier(0.4, 0, 0.2, 1)",
          "&:hover": {
            backgroundColor: theme.palette.mode === "dark"
              ? "rgba(239, 68, 68, 0.1)"
              : "rgba(239, 68, 68, 0.08)",
            transform: "scale(1.05)",
          },
          "&:active": {
            transform: "scale(0.95)",
          },
        }}
      >
        <DeleteIcon sx={{ fontSize: 20 }} />
      </IconButton>
    </Box>
  );
};

export default ManualHoldingRow;